import React from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Modal,
  Row,
  Space,
  Steps,
  Tabs,
  Tag,
  Typography,
  message,
  Spin,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllPricingYears, getPricingYearsByDivision, DivisionPricingYear } from "app/shared/api/getYears";
import { getAllCountries } from "app/shared/api/getCountries";
import { getCentres, CentreListItem, isCentreListItemActive, positiveNumericCentreId } from "app/shared/api/getCentres";
import { DivisionDto, getActiveDivisions } from "app/shared/api/getDivision";
import { PricingYearId } from "app/shared/model/PricingYearId";
import {
  saveDraftPricingYear,
  applyHighSeasonDatesFallback,
  DraftPricingYearDetails,
  DraftPricingYearExistsError,
  PricingYearDateRange,
  PricingYearHighSeasonEntry,
  PricingYearHighSeasonOverride,
} from "app/shared/api/draftPricingYear";
import DateHelper from "app/helpers/dateHelper";
import {
  getSelectedDivisionIdOrUndefined,
  setSelectedDivisionId,
} from "app/shared/division/selectedDivisionStorage";
import "./Page.css";

const { Title, Paragraph, Text } = Typography;

const DATE_FORMAT = "DD-MMM-YYYY";

const EXCLUDED_COUNTRIES = new Set<string>(['Australia', 'New Zealand']);
const EXCLUDED_CENTRES = new Set<string>([]);
const DATE_RANGE_FIELDS = [
  { key: "accommodation", label: "Accommodation Dates" },
  { key: "courses", label: "Course Dates" },
  { key: "addOns", label: "Add Ons Dates" },
  { key: "packages", label: "Package Dates" },
  { key: "transfers", label: "Transfer Dates" },
] as const;

type DateRangeKey = (typeof DATE_RANGE_FIELDS)[number]["key"];

type CategoryKey = "accommodation" | "courses" | "addOns" | "packages";

type DateRangeState = Record<DateRangeKey, RangeValue>;

type RangeValue = {
  from: Dayjs | null;
  to: Dayjs | null;
};

type Centre = {
  id: string;
  numericId: number | undefined;
  name: string;
};

type Country = {
  id: string;
  numericId: number | undefined;
  name: string;
  centres: Centre[];
};

type CategoryState = {
  countries: Record<string, RangeValue>;
  centres: Record<string, Record<string, RangeValue>>;
};

type WizardState = Record<CategoryKey, CategoryState>;

type SerialisedRange = {
  from: string | null;
  to: string | null;
};

type SerialisedCategoryState = {
  countries: Record<string, SerialisedRange>;
  centres: Record<string, Record<string, SerialisedRange>>;
};

type StepOnePayload = {
  pricingYear?: number;
  dates?: Record<string, SerialisedRange>;
  highSeason?: Record<string, SerialisedCategoryState>;
};

const readSelectedDivisionId = (): number | undefined => getSelectedDivisionIdOrUndefined();

const parseDate = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const parseDDMMYYYY = (value: string): Dayjs | null => {
  const trimmed = value.replace(/\s/g, "");
  if (!/^\d{8}$/.test(trimmed)) return null;
  const parsed = dayjs(trimmed, "DDMMYYYY", true);
  return parsed.isValid() ? parsed : null;
};

const createEmptyRange = (): RangeValue => ({ from: null, to: null });
const createEmptyDateRanges = (): DateRangeState => ({
  accommodation: createEmptyRange(),
  courses: createEmptyRange(),
  addOns: createEmptyRange(),
  packages: createEmptyRange(),
  transfers: createEmptyRange(),
});

function getCategoryAcademicBounds(
  ranges: DateRangeState,
  categoryKey: CategoryKey
): { start: Dayjs; end: Dayjs } | null {
  const r = ranges[categoryKey];
  if (!r.from || !r.to) return null;
  return { start: r.from, end: r.to };
}

const buildCatalogue = (
  countries: { id: number | string; name: string; numericId?: number }[],
  centres: CentreListItem[]
): Country[] => {
  const map = new Map<string, Country>();
  countries.forEach((country) => {
       if (EXCLUDED_COUNTRIES.has(country.name)) {
              return;
         }
    const countryCode = (country as any).countryCode ?? (country as any).id ?? String(country.id);
    const id = String(countryCode);
    
    let numericId: number | undefined = country.numericId;
    if (numericId === undefined) {
      numericId = (country as any).countryId;
      if (numericId === undefined && typeof country.id === 'number') {
        numericId = country.id;
      } else if (numericId === undefined && typeof country.id === 'string') {
        const parsed = Number(country.id);
        numericId = !isNaN(parsed) ? parsed : undefined;
      }
    }
    
    map.set(id, { id, numericId, name: country.name, centres: [] });
  });

  centres.forEach((centre) => {
       if (EXCLUDED_CENTRES.has(centre.name)) {
             return;
            }
    if (!isCentreListItemActive(centre)) return;
    const countryCode = centre.countryCode ?? (centre as any).countryId ?? (centre as any).country;
    const countryId = countryCode != null ? String(countryCode) : undefined;
    if (!countryId) return;
    const target = map.get(countryId);
    if (!target) return;
    
    let centreCountryId: number | undefined = (centre as any).countryId;
    if (centreCountryId === undefined) {
      centreCountryId = (centre as any).countryNumericId;
    }
    if (centreCountryId === undefined) {
      centreCountryId = (centre as any).country?.id;
    }
    if (centreCountryId === undefined) {
      centreCountryId = (centre as any).country?.countryId;
    }
    
    if (typeof centreCountryId === 'number' && centreCountryId > 0 && target.numericId === undefined) {
      target.numericId = centreCountryId;
    }
    
    const centreNumericId = positiveNumericCentreId(centre) ?? undefined;
    target.centres.push({ 
      id: String(centre.id), 
      numericId: centreNumericId,
      name: centre.name,
    });
  });

  const result = Array.from(map.values())
      .filter((country) => country.centres.length > 0)
          .map((country) => ({
      ...country,
      centres: [...country.centres].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  return result;
};

const createInitialCategoryState = (catalogue: Country[]): CategoryState => {
  const countries = catalogue.reduce<Record<string, RangeValue>>((acc, country) => {
    acc[country.id] = createEmptyRange();
    return acc;
  }, {});

  const centres = catalogue.reduce<Record<string, Record<string, RangeValue>>>((acc, country) => {
    acc[country.id] = country.centres.reduce<Record<string, RangeValue>>((centreAcc, centre) => {
      centreAcc[centre.id] = createEmptyRange();
      return centreAcc;
    }, {});
    return acc;
  }, {});

  return { countries, centres };
};

const cloneCategoryState = (state: CategoryState): CategoryState => ({
  countries: Object.fromEntries(Object.entries(state.countries).map(([key, value]) => [key, { ...value }])),
  centres: Object.fromEntries(
    Object.entries(state.centres).map(([countryId, centreMap]) => [
      countryId,
      Object.fromEntries(Object.entries(centreMap).map(([centreId, value]) => [centreId, { ...value }]))
    ])
  ),
});

const hydrateCategoryState = (
  catalogue: Country[],
  source?: SerialisedCategoryState
): CategoryState => {
  const initial = createInitialCategoryState(catalogue);
  if (!source) return initial;

  const hydrated = cloneCategoryState(initial);

  Object.entries(source.countries ?? {}).forEach(([countryId, range]) => {
    if (!hydrated.countries[countryId]) return;
    hydrated.countries[countryId] = {
      from: parseDate(range.from),
      to: parseDate(range.to),
    };
  });

  Object.entries(source.centres ?? {}).forEach(([countryId, centreMap]) => {
    if (!hydrated.centres[countryId]) return;
    Object.entries(centreMap ?? {}).forEach(([centreId, range]) => {
      hydrated.centres[countryId][centreId] = {
        from: parseDate(range.from),
        to: parseDate(range.to),
      };
    });
  });

  return hydrated;
};

const buildInitialWizardState = (
  catalogue: Country[],
  stored?: Record<string, SerialisedCategoryState>
): WizardState => {
  const base = {
    accommodation: createInitialCategoryState(catalogue),
    courses: createInitialCategoryState(catalogue),
    addOns: createInitialCategoryState(catalogue),
    packages: createInitialCategoryState(catalogue),
  } as WizardState;

  if (stored) {
    (Object.keys(base) as CategoryKey[]).forEach((key) => {
      const storedCategory = stored[key];
      if (storedCategory) {
        base[key] = hydrateCategoryState(catalogue, storedCategory);
      }
    });
  }

  return base;
};

const serialiseWizardState = (state: WizardState): Record<string, SerialisedCategoryState> => {
  const result: Record<string, SerialisedCategoryState> = {};
  (Object.keys(state) as CategoryKey[]).forEach((key) => {
    const categoryState = state[key];
    const countries: Record<string, SerialisedRange> = {};
    Object.entries(categoryState.countries).forEach(([countryId, range]) => {
      countries[countryId] = {
        from: range.from ? range.from.format("YYYY-MM-DD") : null,
        to: range.to ? range.to.format("YYYY-MM-DD") : null,
      };
    });

    const centres: Record<string, Record<string, SerialisedRange>> = {};
    Object.entries(categoryState.centres).forEach(([countryId, centreMap]) => {
      centres[countryId] = {};
      Object.entries(centreMap).forEach(([centreId, range]) => {
        centres[countryId][centreId] = {
          from: range.from ? range.from.format("YYYY-MM-DD") : null,
          to: range.to ? range.to.format("YYYY-MM-DD") : null,
        };
      });
    });

    result[key] = { countries, centres };
  });
  return result;
};

const resolveCentreNumericId = (centre: Centre): number | undefined => {
  if (centre.numericId != null && !isNaN(centre.numericId) && centre.numericId > 0) {
    return centre.numericId;
  }
  if (typeof centre.id !== "string") return undefined;
  const cnMatch = /^CN(\d+)$/i.exec(centre.id);
  if (cnMatch) {
    const parsed = parseInt(cnMatch[1], 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : undefined;
  }
  const parsed = Number(centre.id);
  return !isNaN(parsed) && parsed > 0 ? parsed : undefined;
};

const hasCompleteRange = (range?: RangeValue): range is RangeValue & { from: Dayjs; to: Dayjs } =>
  !!(range?.from && range?.to);

const toOverride = (
  centre: Centre,
  range?: RangeValue
): PricingYearHighSeasonOverride | null => {
  if (!hasCompleteRange(range)) return null;
  const centreNumericId = resolveCentreNumericId(centre);
  if (centreNumericId == null) return null;
  return {
    centreId: centreNumericId,
    dateFrom: range.from.format("YYYY-MM-DD"),
    dateTo: range.to.format("YYYY-MM-DD"),
  };
};

const buildHighSeasonPayload = (
  catalogue: Country[],
  state: WizardState,
  academicYearDates: DateRangeState
): DraftPricingYearDetails["highSeasonDates"] => {
  const buildEntries = (key: CategoryKey): PricingYearHighSeasonEntry[] => {
    const categoryState = state[key];
    const academic = getCategoryAcademicBounds(academicYearDates, key);
    const academicRange: RangeValue | undefined = academic
      ? { from: academic.start, to: academic.end }
      : undefined;
    const entries: PricingYearHighSeasonEntry[] = [];

    catalogue.forEach((country) => {
      const centreRanges = categoryState.centres[country.id] ?? {};
      const centresWithDates = country.centres.filter((centre) =>
        hasCompleteRange(centreRanges[centre.id])
      );
      if (centresWithDates.length === 0) return;

      const firstRange = centreRanges[centresWithDates[0].id];
      if (!hasCompleteRange(firstRange)) return;

      const allCentresHaveHighSeason =
        country.centres.length > 0 &&
        centresWithDates.length === country.centres.length;

      const countryRange = allCentresHaveHighSeason || !hasCompleteRange(academicRange)
        ? firstRange
        : academicRange;

      const overrides = country.centres
        .map((centre) => {
          const configured = centreRanges[centre.id];
          const range = hasCompleteRange(configured)
            ? configured
            : academicRange;
          return toOverride(centre, range);
        })
        .filter((value): value is PricingYearHighSeasonOverride => !!value);

      const entry: PricingYearHighSeasonEntry = {
        CountryIsoCode: country.id,
        dateFrom: countryRange.from.format("YYYY-MM-DD"),
        dateTo: countryRange.to.format("YYYY-MM-DD"),
      };
      if (overrides.length) {
        entry.centreOverrides = overrides;
      }
      entries.push(entry);
    });
    return entries;
  };

  const result: DraftPricingYearDetails["highSeasonDates"] = {
    accommodation: buildEntries("accommodation"),
    courses: buildEntries("courses"),
    addOns: buildEntries("addOns"),
    transfers: [],
    packages: buildEntries("packages"),
  };

  return applyHighSeasonDatesFallback(result);
};

const categories: Array<{ key: CategoryKey; label: string }> = [
  { key: "accommodation", label: "Accommodation" },
  { key: "courses", label: "Courses" },
  { key: "addOns", label: "Add Ons" },
  { key: "packages", label: "Packages" },
];

const WizardStepTwo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateFromStepOne: StepOnePayload = (location.state as StepOnePayload | undefined) ?? {};

  const [catalogue, setCatalogue] = React.useState<Country[]>([]);
  const [catalogueLoading, setCatalogueLoading] = React.useState(false);
  const [catalogueError, setCatalogueError] = React.useState<string | undefined>();

  const [divisions, setDivisions] = React.useState<DivisionDto[]>([]);
  const [divisionsLoading, setDivisionsLoading] = React.useState(false);
  const [divisionError, setDivisionError] = React.useState<string | undefined>();

  const [existingYears, setExistingYears] = React.useState<PricingYearId[]>([]);
  const [statusLoading, setStatusLoading] = React.useState(false);
  const [statusError, setStatusError] = React.useState<string | undefined>();
  const [nextLoading, setNextLoading] = React.useState(false);

  const [hasDraft, setHasDraft] = React.useState(false);
  const [draftYear, setDraftYear] = React.useState<number | undefined>();

  const [dateRanges, setDateRanges] = React.useState<DateRangeState>(createEmptyDateRanges());

  const [wizardState, setWizardState] = React.useState<WizardState>(() =>
    buildInitialWizardState([], stateFromStepOne.highSeason)
  );
  const [activeTab, setActiveTab] = React.useState<CategoryKey>("accommodation");
  const [setDatesFrom, setSetDatesFrom] = React.useState<Dayjs | null>(null);
  const [setDatesTo, setSetDatesTo] = React.useState<Dayjs | null>(null);
  const [selectedCentreIds, setSelectedCentreIds] = React.useState<Record<string, string[]>>({});
  const [countryExpandedKeys, setCountryExpandedKeys] = React.useState<string[]>([]);

  const [divisionId, setDivisionId] = React.useState<number | undefined>(readSelectedDivisionId);
  const prevDivisionForRedirectRef = React.useRef(divisionId);

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      const next = readSelectedDivisionId();
      setDivisionId((prev) => (next === prev ? prev : next));
    }, 400);
    return () => window.clearInterval(intervalId);
  }, []);

  React.useEffect(() => {
    const prev = prevDivisionForRedirectRef.current;
    prevDivisionForRedirectRef.current = divisionId;
    if (prev === divisionId) return;
    if (prev === undefined || divisionId === undefined) return;
    if (!location.pathname.includes("/step-2")) return;
    const st = (location.state as { pricingYear?: number } | undefined) ?? {};
    navigate("/new-pricing-year/wizard", {
      replace: true,
      state: { pricingYear: st.pricingYear ?? new Date().getFullYear() + 1 },
    });
    message.info("Division changed. Returning to step 1.");
  }, [divisionId, location.pathname, location.state, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    setCatalogueLoading(true);
    setCatalogueError(undefined);

    Promise.all([getAllCountries(), getCentres()])

      .then(([countries, centres]) => {
        if (cancelled) return;
        const mapped = buildCatalogue(countries ?? [], centres ?? []);
        setCatalogue(mapped);
        if (!mapped.length) {
          setCatalogueError("No countries or centres available. Configure reference data first.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const text = err instanceof Error ? err.message : "Failed to load countries and centres";
        setCatalogueError(text);
        setCatalogue([]);
      })
      .finally(() => {
        if (!cancelled) {
          setCatalogueLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setDivisionsLoading(true);
    setDivisionError(undefined);

    getActiveDivisions()
      .then((items) => {
        if (!cancelled) {
          setDivisions(items ?? []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const text = err instanceof Error ? err.message : "Failed to load divisions";
          setDivisionError(text);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDivisionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const initialiseWizardStateFromLocation = React.useCallback(() => {
    setWizardState(buildInitialWizardState(catalogue, stateFromStepOne.highSeason));
  }, [catalogue, stateFromStepOne.highSeason]);

  React.useEffect(() => {
    if (!catalogue.length) return;
    initialiseWizardStateFromLocation();
  }, [catalogue, initialiseWizardStateFromLocation]);

  const refreshDivisionData = React.useCallback(
    async (currentDivisionId: number) => {
      setStatusLoading(true);
      setStatusError(undefined);
      try {
        const [years, divisionYears] = await Promise.all([
          getAllPricingYears(currentDivisionId),
          getPricingYearsByDivision(currentDivisionId),
        ]);

        setExistingYears(years ?? []);

        const draftYearData = divisionYears?.find((py: DivisionPricingYear) => {
          if (typeof py.isDraft === 'boolean') {
            return py.isDraft;
          }
          const statusCandidates = [py.status, py.state, py.lifecycleStatus, py.statusName];
          return statusCandidates
            .filter((value): value is string => typeof value === 'string')
            .some((status) => status.toLowerCase().includes('draft'));
        });

        if (draftYearData) {
          setHasDraft(true);
          setDraftYear(draftYearData.year);
        } else {
          setHasDraft(false);
          setDraftYear(undefined);
        }
        
        initialiseWizardStateFromLocation();
        
        if (stateFromStepOne.dates) {
          const dateRangesFromState: DateRangeState = {
            accommodation: {
              from: parseDate(stateFromStepOne.dates.accommodation?.from),
              to: parseDate(stateFromStepOne.dates.accommodation?.to),
            },
            courses: {
              from: parseDate(stateFromStepOne.dates.courses?.from),
              to: parseDate(stateFromStepOne.dates.courses?.to),
            },
            addOns: {
              from: parseDate(stateFromStepOne.dates.addOns?.from),
              to: parseDate(stateFromStepOne.dates.addOns?.to),
            },
            transfers: {
              from: parseDate(stateFromStepOne.dates.transfers?.from),
              to: parseDate(stateFromStepOne.dates.transfers?.to),
            },
            packages: {
              from: parseDate(stateFromStepOne.dates.packages?.from),
              to: parseDate(stateFromStepOne.dates.packages?.to),
            },
          };
          setDateRanges(dateRangesFromState);
        } else {
          setDateRanges(createEmptyDateRanges());
        }
      } catch (err) {
        const text = err instanceof Error ? err.message : "Failed to load pricing year data";
        setStatusError(text);
        setExistingYears([]);
        setHasDraft(false);
        setDraftYear(undefined);
        setDateRanges(createEmptyDateRanges());
        initialiseWizardStateFromLocation();
      } finally {
        setStatusLoading(false);
      }
    },
    [catalogue, initialiseWizardStateFromLocation, stateFromStepOne.dates]
  );

  React.useEffect(() => {
    if (!divisionId || !catalogue.length || catalogueError) {
      return;
    }
    refreshDivisionData(divisionId);
  }, [divisionId, catalogue, catalogueError, refreshDivisionData]);

  const targetYear = React.useMemo(() => {
    if (draftYear) return draftYear;
    if (existingYears.length) {
      return Math.max(...existingYears.map((item) => item.year)) + 1;
    }
    return dayjs().add(1, "year").year();
  }, [draftYear, existingYears]);

  const updateCountryRange = React.useCallback(
    (categoryKey: CategoryKey, countryId: string, part: "from" | "to", value: Dayjs | null) => {
      setWizardState((prev) => {
        const categoryState = prev[categoryKey];
        const updated = cloneCategoryState(categoryState);
        
        updated.countries[countryId] = {
          ...updated.countries[countryId],
          [part]: value,
        };
        
        const country = catalogue.find((c) => c.id === countryId);
        if (country && country.centres.length > 0) {
          if (!updated.centres[countryId]) {
            updated.centres[countryId] = {};
          }
          
          country.centres.forEach((centre) => {
            if (!updated.centres[countryId][centre.id]) {
              updated.centres[countryId][centre.id] = createEmptyRange();
            }
            updated.centres[countryId][centre.id] = {
              ...updated.centres[countryId][centre.id],
              [part]: value,
            };
          });
        }
        
        return {
          ...prev,
          [categoryKey]: updated,
        };
      });
    },
    [catalogue]
  );

  const updateCentreRange = React.useCallback(
    (categoryKey: CategoryKey, countryId: string, centreId: string, part: "from" | "to", value: Dayjs | null) => {
      setWizardState((prev) => {
        const categoryState = prev[categoryKey];
        const updated = cloneCategoryState(categoryState);
        if (!updated.centres[countryId]) {
          updated.centres[countryId] = {};
        }
        if (!updated.centres[countryId][centreId]) {
          updated.centres[countryId][centreId] = createEmptyRange();
        }
        updated.centres[countryId][centreId] = {
          ...updated.centres[countryId][centreId],
          [part]: value,
        };
        return {
          ...prev,
          [categoryKey]: updated,
        };
      });
    },
    []
  );

  const setCountrySelection = React.useCallback((countryId: string, selected: boolean) => {
    const country = catalogue.find((c) => c.id === countryId);
    if (!country) return;
    setSelectedCentreIds((prev) => ({
      ...prev,
      [countryId]: selected ? country.centres.map((c) => c.id) : [],
    }));
  }, [catalogue]);

  const setCentreSelection = React.useCallback((countryId: string, centreId: string, selected: boolean) => {
    setSelectedCentreIds((prev) => {
      const list = prev[countryId] ?? [];
      const next = selected
        ? (list.includes(centreId) ? list : [...list, centreId])
        : list.filter((id) => id !== centreId);
      return { ...prev, [countryId]: next };
    });
  }, []);

  const hasAnyCentreSelected = React.useMemo(
    () => Object.values(selectedCentreIds).some((ids) => ids.length > 0),
    [selectedCentreIds]
  );

  const applySetDatesToSelected = React.useCallback(() => {
    if (!setDatesFrom || !setDatesTo || !hasAnyCentreSelected) return;
    setWizardState((prev) => {
      const categoryKey = activeTab;
      const categoryState = prev[categoryKey];
      const updated = cloneCategoryState(categoryState);
      Object.entries(selectedCentreIds).forEach(([countryId, centreIds]) => {
        centreIds.forEach((centreId) => {
          if (!updated.centres[countryId]) updated.centres[countryId] = {};
          if (!updated.centres[countryId][centreId]) updated.centres[countryId][centreId] = createEmptyRange();
          updated.centres[countryId][centreId] = { from: setDatesFrom, to: setDatesTo };
        });
      });
      return { ...prev, [categoryKey]: updated };
    });
  }, [activeTab, setDatesFrom, setDatesTo, selectedCentreIds, hasAnyCentreSelected]);

  const collectValidationErrors = React.useCallback(
    (categoryKey: CategoryKey) => {
      const bounds = getCategoryAcademicBounds(dateRanges, categoryKey);
      const categoryState = wizardState[categoryKey];
      const countryErrors: Record<string, string | undefined> = {};
      const centreErrors: Record<string, Record<string, string | undefined>> = {};

      const rangeMessage = bounds
        ? `Dates must stay within ${bounds.start.format(DATE_FORMAT)} and ${bounds.end.format(DATE_FORMAT)}.`
        : "Configure academic dates for this product in step 1 first.";

      Object.entries(categoryState.countries).forEach(([countryId, range]) => {
        if (range.from && range.to && range.to.isBefore(range.from, "day")) {
          countryErrors[countryId] = '"Date To" must be after "Date From".';
        } else if ((range.from && !range.to) || (!range.from && range.to)) {
          countryErrors[countryId] = "Enter both start and end dates.";
        } else if (bounds && range.from && range.to) {
          if (
            range.from.isBefore(bounds.start, "day") ||
            range.from.isAfter(bounds.end, "day") ||
            range.to.isBefore(bounds.start, "day") ||
            range.to.isAfter(bounds.end, "day")
          ) {
            countryErrors[countryId] = rangeMessage;
          } else {
            countryErrors[countryId] = undefined;
          }
        } else {
          countryErrors[countryId] = undefined;
        }
      });

      Object.entries(categoryState.centres).forEach(([countryId, centreMap]) => {
        centreErrors[countryId] = centreErrors[countryId] ?? {};
        Object.entries(centreMap).forEach(([centreId, range]) => {
          if (range.from && range.to && range.to.isBefore(range.from, "day")) {
            centreErrors[countryId][centreId] = '"Date To" must be after "Date From".';
          } else if ((range.from && !range.to) || (!range.from && range.to)) {
            centreErrors[countryId][centreId] = "Enter both start and end dates.";
          } else if (bounds && range.from && range.to) {
            if (
              range.from.isBefore(bounds.start, "day") ||
              range.from.isAfter(bounds.end, "day") ||
              range.to.isBefore(bounds.start, "day") ||
              range.to.isAfter(bounds.end, "day")
            ) {
              centreErrors[countryId][centreId] = rangeMessage;
            } else {
              centreErrors[countryId][centreId] = undefined;
            }
          } else {
            centreErrors[countryId][centreId] = undefined;
          }
        });
      });

      return { countryErrors, centreErrors };
    },
    [wizardState, dateRanges]
  );

  const validationMessages = React.useMemo(() => {
    const errors: string[] = [];

    (Object.keys(wizardState) as CategoryKey[]).forEach((categoryKey) => {
      const bounds = getCategoryAcademicBounds(dateRanges, categoryKey);
      const categoryState = wizardState[categoryKey];

      Object.entries(categoryState.countries).forEach(([countryId, range]) => {
        if (range.from && range.to) {
          if (!range.to.isAfter(range.from, "day")) {
            errors.push(`"Date To" must be after "Date From" for ${categoryKey} in country ${countryId}.`);
          }
          if (bounds) {
            if (range.from.isBefore(bounds.start, "day") || range.from.isAfter(bounds.end, "day")) {
              errors.push(`Start date for ${categoryKey} in country ${countryId} is outside the step 1 academic range.`);
            }
            if (range.to.isBefore(bounds.start, "day") || range.to.isAfter(bounds.end, "day")) {
              errors.push(`End date for ${categoryKey} in country ${countryId} is outside the step 1 academic range.`);
            }
          }
        } else if (range.from || range.to) {
          errors.push(`Enter both start and end dates for ${categoryKey} in country ${countryId}.`);
        }
      });

      Object.entries(categoryState.centres).forEach(([countryId, centreMap]) => {
        Object.entries(centreMap).forEach(([centreId, range]) => {
          if (range.from && range.to) {
            if (!range.to.isAfter(range.from, "day")) {
              errors.push(`"Date To" must be after "Date From" for ${categoryKey} in centre ${centreId}.`);
            }
            if (bounds) {
              if (range.from.isBefore(bounds.start, "day") || range.from.isAfter(bounds.end, "day")) {
                errors.push(`Start date for ${categoryKey} in centre ${centreId} is outside the step 1 academic range.`);
              }
              if (range.to.isBefore(bounds.start, "day") || range.to.isAfter(bounds.end, "day")) {
                errors.push(`End date for ${categoryKey} in centre ${centreId} is outside the step 1 academic range.`);
              }
            }
          } else if (range.from || range.to) {
            errors.push(`Enter both start and end dates for ${categoryKey} in centre ${centreId}.`);
          }
        });
      });
    });

    return errors;
  }, [wizardState, dateRanges]);

  const hasAnyErrors = validationMessages.length > 0;

  const buildSavePayload = React.useCallback(
    (division: number, year: number): DraftPricingYearDetails => {
      const toPayloadRange = (key: DateRangeKey): PricingYearDateRange | null => {
        const { from, to } = dateRanges[key];
        if (!from || !to) return null;
        return {
          dateFrom: from.format("YYYY-MM-DD"),
          dateTo: to.format("YYYY-MM-DD"),
        };
      };

      return {
        divisionId: division,
        year,
        academicYearDates: {
          courses: toPayloadRange("courses"),
          accommodation: toPayloadRange("accommodation"),
          addOns: toPayloadRange("addOns"),
          transfers: toPayloadRange("transfers"),
          packages: toPayloadRange("packages"),
        },
        highSeasonDates: buildHighSeasonPayload(catalogue, wizardState, dateRanges),
      };
    },
    [catalogue, dateRanges, wizardState]
  );

  const handleNext = React.useCallback(() => {
    if (!divisionId) {
      message.error("Select a division before continuing.");
      return;
    }
    if (hasAnyErrors) {
      Modal.warning({
        title: "Fix validation issues",
        content: "Resolve the highlighted date errors before proceeding to the Draft Pricelist.",
      });
      return;
    }

    const currentDivisionId = divisionId;
    const year = draftYear ?? targetYear;
    setNextLoading(true);

    const payload = buildSavePayload(currentDivisionId, year);

    saveDraftPricingYear(currentDivisionId, payload)
      .then(() => {
        message.success("Draft pricing year saved");
        setSelectedDivisionId(currentDivisionId);
        navigate("/pricelist/draft", {
          state: {
            divisionId: currentDivisionId,
            year,
            highSeason: serialiseWizardState(wizardState),
          },
        });
      })
      .catch((err) => {
        const errorMessages = (err as any)?.errorMessages;
        if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
          const parsedMessages = errorMessages.map((msg: string) => {
            if (typeof msg === 'string') {
              try {
                const parsed = JSON.parse(msg);
                if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.message) {
                  return parsed[0].message;
                }
                if (parsed?.message) {
                  return parsed.message;
                }
              } catch {
              }
            }
            return msg;
          }).filter((msg: string) => msg && msg.trim());
          
          const uniqueMessages = Array.from(new Set(parsedMessages));
          
          if (uniqueMessages.length > 0) {
            const displayMessage = uniqueMessages[0];
            message.error(displayMessage);
          } else {
            const text = err instanceof Error ? err.message : "Failed to save draft pricing year";
            message.error(text);
          }
        } else {
          const text = err instanceof Error ? err.message : "Failed to save draft pricing year";
          message.error(text);
        }
      })
      .finally(() => {
        setNextLoading(false);
      });
  }, [divisionId, buildSavePayload, hasAnyErrors, draftYear, targetYear, wizardState, navigate]);

  const handleBack = React.useCallback(() => {
    navigate("/new-pricing-year/wizard", {
      state: {
        pricingYear: draftYear ?? targetYear,
        stepOneDates: stateFromStepOne.dates,
        highSeason: serialiseWizardState(wizardState),
      },
    });
  }, [draftYear, targetYear, stateFromStepOne.dates, wizardState, navigate]);

  const disabledDateForCategory = React.useCallback(
    (categoryKey: CategoryKey) => {
      const bounds = getCategoryAcademicBounds(dateRanges, categoryKey);
      return (current: Dayjs) => {
        if (!current) return true;
        if (!bounds) return true;
        return current.isBefore(bounds.start, "day") || current.isAfter(bounds.end, "day");
      };
    },
    [dateRanges]
  );

  const activeAcademicBoundsForInfo = React.useMemo(
    () => getCategoryAcademicBounds(dateRanges, activeTab),
    [dateRanges, activeTab]
  );

  const activeProductLabel = categories.find((c) => c.key === activeTab)?.label ?? "this product";

  const highSeasonRangeInfoMessage = activeAcademicBoundsForInfo
    ? `For ${activeProductLabel}, dates must stay between ${activeAcademicBoundsForInfo.start.format(
        DATE_FORMAT
      )} and ${activeAcademicBoundsForInfo.end.format(DATE_FORMAT)} (the dates set in step 1).`
    : `Configure academic dates for ${activeProductLabel} in step 1 before choosing high season dates here.`;

  const getCountrySummary = (categoryKey: CategoryKey, country: Country): string => {
    const centreRanges = (wizardState[categoryKey].centres[country.id] ?? {});
    const ranges = country.centres
      .map((c) => centreRanges[c.id])
      .filter((r): r is RangeValue => !!r && r.from != null && r.to != null);
    if (ranges.length === 0) return "";
    const firstRange = ranges[0];
    if (!firstRange || firstRange.from == null || firstRange.to == null) return "";
    const fromFirst = firstRange.from;
    const toFirst = firstRange.to;
    const first = `${fromFirst.format(DATE_FORMAT)} - ${toFirst.format(DATE_FORMAT)}`;
    const allSame = ranges.every(
      (r) => r.from != null && r.to != null && r.from.isSame(fromFirst, "day") && r.to.isSame(toFirst, "day")
    );
    return allSame ? first : "multiple dates selected";
  };

  const handleCentreDateBlur = (
    categoryKey: CategoryKey,
    countryId: string,
    centreId: string,
    part: "from" | "to"
  ) => (e: React.FocusEvent<HTMLElement>) => {
    const value = (e.target as HTMLInputElement)?.value?.replace(/\s/g, "") ?? "";
    if (!/^\d{8}$/.test(value)) return;
    const parsed = parseDDMMYYYY(value);
    if (parsed) updateCentreRange(categoryKey, countryId, centreId, part, parsed);
  };

  const handleCentreDateKeyDown = (
    categoryKey: CategoryKey,
    countryId: string,
    centreId: string,
    part: "from" | "to"
  ) => (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    const value = (e.target as HTMLInputElement)?.value?.replace(/\s/g, "") ?? "";
    if (!/^\d{8}$/.test(value)) return;
    const parsed = parseDDMMYYYY(value);
    if (parsed) {
      e.preventDefault();
      updateCentreRange(categoryKey, countryId, centreId, part, parsed);
    }
  };

  const renderCentreRow = (
    categoryKey: CategoryKey,
    countryId: string,
    centre: Centre,
    centreErrors: Record<string, Record<string, string | undefined>>
  ) => {
    const value = wizardState[categoryKey].centres[countryId]?.[centre.id] ?? createEmptyRange();
    const error = centreErrors[countryId]?.[centre.id];
    const isChecked = (selectedCentreIds[countryId] ?? []).includes(centre.id);

    return (
      <div key={centre.id} style={{ marginBottom: 20 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>{centre.name}</Text>
        <Space align="start" size={16} wrap>
          <Checkbox
            checked={isChecked}
            onChange={(e) => setCentreSelection(countryId, centre.id, e.target.checked)}
            aria-label={`Select ${centre.name}`}
            style={{ marginTop: 4 }}
          />
          <Space size={12} wrap>
            <DatePicker
              placeholder="Date From"
              value={value.from}
              onChange={(val) => updateCentreRange(categoryKey, countryId, centre.id, "from", val)}
              onBlur={handleCentreDateBlur(categoryKey, countryId, centre.id, "from")}
              onKeyDown={handleCentreDateKeyDown(categoryKey, countryId, centre.id, "from")}
              format={DateHelper.DATE_PICKER_FORMATS}
              allowClear
              disabledDate={disabledDateForCategory(categoryKey)}
              defaultPickerValue={value.from ?? getCategoryAcademicBounds(dateRanges, categoryKey)?.start}
              style={{ width: 180 }}
            />
            <DatePicker
              placeholder="Date To"
              value={value.to}
              onChange={(val) => updateCentreRange(categoryKey, countryId, centre.id, "to", val)}
              onBlur={handleCentreDateBlur(categoryKey, countryId, centre.id, "to")}
              onKeyDown={handleCentreDateKeyDown(categoryKey, countryId, centre.id, "to")}
              format={DateHelper.DATE_PICKER_FORMATS}
              allowClear
              disabledDate={disabledDateForCategory(categoryKey)}
              defaultPickerValue={
                value.to ?? value.from ?? getCategoryAcademicBounds(dateRanges, categoryKey)?.start
              }
              style={{ width: 180 }}
            />
          </Space>
        </Space>
        {error && (
          <Text type="danger" style={{ display: "block", marginTop: 4 }}>
            {error}
          </Text>
        )}
      </div>
    );
  };

  const renderCountryCard = (
    categoryKey: CategoryKey,
    country: Country,
    _countryErrors: Record<string, string | undefined>,
    centreErrors: Record<string, Record<string, string | undefined>>
  ) => {
    const selectedIds = selectedCentreIds[country.id] ?? [];
    const allCentreIds = country.centres.map((c) => c.id);
    const countryChecked = allCentreIds.length > 0 && selectedIds.length === allCentreIds.length;
    const countryIndeterminate = selectedIds.length > 0 && selectedIds.length < allCentreIds.length;
    const summary = getCountrySummary(categoryKey, country);

    return (
      <Collapse.Panel
        key={country.id}
        header={
          <Space onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={countryChecked}
              indeterminate={countryIndeterminate}
              onChange={(e) => setCountrySelection(country.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select all centres in ${country.name}`}
              style={{ marginTop: 6 }}
            />
            <span>{country.name}</span>
            {summary && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {summary}
              </Text>
            )}
          </Space>
        }
      >
        <Space orientation="vertical" size={16} style={{ width: "100%", paddingLeft: 24 }}>
          {country.centres.map((centre) => renderCentreRow(categoryKey, country.id, centre, centreErrors))}
        </Space>
      </Collapse.Panel>
    );
  };

  const handleSetDatesBlur = (part: "from" | "to") => (e: React.FocusEvent<HTMLElement>) => {
    const value = (e.target as HTMLInputElement)?.value?.replace(/\s/g, "") ?? "";
    if (!/^\d{8}$/.test(value)) return;
    const parsed = parseDDMMYYYY(value);
    if (parsed) (part === "from" ? setSetDatesFrom : setSetDatesTo)(parsed);
  };

  const handleSetDatesKeyDown = (part: "from" | "to") => (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    const value = (e.target as HTMLInputElement)?.value?.replace(/\s/g, "") ?? "";
    if (!/^\d{8}$/.test(value)) return;
    const parsed = parseDDMMYYYY(value);
    if (parsed) {
      e.preventDefault();
      (part === "from" ? setSetDatesFrom : setSetDatesTo)(parsed);
    }
  };

  const tabItems = categories.map(({ key, label }) => {
    const { countryErrors, centreErrors } = collectValidationErrors(key);
    return {
      key,
      label,
      children: (
        <Space orientation="vertical" size={24} style={{ width: "100%" }}>
          <Space size={12} align="center" wrap>
            <Text strong>Set Dates</Text>
            <DatePicker
              placeholder="Date From"
              value={setDatesFrom}
              onChange={setSetDatesFrom}
              onBlur={handleSetDatesBlur("from")}
              onKeyDown={handleSetDatesKeyDown("from")}
              format={DateHelper.DATE_PICKER_FORMATS}
              allowClear
              disabledDate={disabledDateForCategory(key)}
              defaultPickerValue={
                setDatesFrom ?? getCategoryAcademicBounds(dateRanges, key)?.start
              }
              style={{ width: 180 }}
            />
            <DatePicker
              placeholder="Date To"
              value={setDatesTo}
              onChange={setSetDatesTo}
              onBlur={handleSetDatesBlur("to")}
              onKeyDown={handleSetDatesKeyDown("to")}
              format={DateHelper.DATE_PICKER_FORMATS}
              allowClear
              disabledDate={disabledDateForCategory(key)}
              defaultPickerValue={
                setDatesTo ?? setDatesFrom ?? getCategoryAcademicBounds(dateRanges, key)?.start
              }
              style={{ width: 180 }}
            />
            <Button
              type="default"
              onClick={applySetDatesToSelected}
              disabled={!setDatesFrom || !setDatesTo || !hasAnyCentreSelected}
            >
              Apply to selected
            </Button>
          </Space>
          <Collapse
            className="high-season-dates-collapse"
            activeKey={countryExpandedKeys}
            onChange={(keys) => setCountryExpandedKeys(Array.isArray(keys) ? keys : [keys])}
          >
            {catalogue.map((country) => renderCountryCard(key, country, countryErrors, centreErrors))}
          </Collapse>
        </Space>
      ),
    };
  });

  const existingYearsSummary = existingYears.length
    ? existingYears.map((year) => year.year).sort((a, b) => a - b).join(", ")
    : "None yet";

  const pricingYearHeading = draftYear ?? stateFromStepOne.pricingYear ?? targetYear;

  if (catalogueLoading || divisionsLoading || (statusLoading && !hasDraft)) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "60vh",
        width: "100%"
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "32px 16px" }}>
      <div style={{ width: "100%", maxWidth: 1100 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          Back
        </Button>

        <Space align="center" size={16} style={{ marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            New Pricing Year <Text>{pricingYearHeading}</Text>
          </Title>
          <Tag color="processing">Draft</Tag>
        </Space>

        <Steps
          current={1}
          style={{ marginBottom: 32 }}
          items={[
            { title: "Pick Academic Year Dates" },
            { title: "High Season Dates" },
          ]}
        />

        <Card styles={{ body: { padding: 32 } }} style={{ marginBottom: 24 }}>
          {catalogueError && (
            <Alert
              style={{ marginBottom: 24 }}
              type="error"
              showIcon
              title="Unable to load countries and centres"
              description={catalogueError}
            />
          )}

          {!divisionId && (
            <Alert
              style={{ marginBottom: 24 }}
              type="warning"
              showIcon
              title="No division selected"
              description="Choose a division from the header so the wizard can scope the pricing year correctly."
            />
          )}

          {statusError && (
            <Alert
              style={{ marginBottom: 24 }}
              type="error"
              showIcon
              title="Unable to load pricing year data"
              description={statusError}
            />
          )}

          <Row gutter={[32, 32]}>
            <Col xs={24} lg={14}>
              <Title level={4} style={{ marginBottom: 12 }}>
                Step 2: High Season Dates
              </Title>
              <Paragraph style={{ marginBottom: 0 }}>
                Enter high season dates for this centre/country. Use the tabs below to configure pricing by product category
                (Courses, Accommodation, Add-ons, Packages). Within each tab, you can choose which centres the high season
                dates should apply to. Leave a centre unconfigured if high season pricing shouldn't apply there.
              </Paragraph>
            </Col>
            <Col xs={24} lg={10}>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Note: You can update this later, but any changes will reset prices for the affected product category and
                centre (e.g. Accommodation in Brighton).
              </Paragraph>
            </Col>
          </Row>

          <Alert
            style={{ marginTop: 24 }}
            type="info"
            showIcon
            title={highSeasonRangeInfoMessage}
          />

          <div style={{ marginTop: 32 }}>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as CategoryKey)}
              tabPosition="bottom"
              items={tabItems}
            />
          </div>
        </Card>

        <div className="new-pricing-year-card-actions">
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            disabled={hasAnyErrors || !catalogue.length || !divisionId}
            loading={nextLoading}
          >
            Save and Proceed to Draft Pricelist
          </Button>
        </div>

        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary">
            Existing pricing years: {existingYearsSummary}
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

export default WizardStepTwo;
